import { useState } from 'react'

import { CallResolution, IDate } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { pluralizeS } from '@avantstay/backoffice-core'

import { clipText } from '@new/domains/operations/utils/text'

import { zendeskTicketUrl } from '../../StayTimeline.consts'
import { GuestInboxCall } from '../../StayTimeline.types'
import { getEventWhen } from '../../StayTimeline.utils'
import { Pill } from '../Shared.styles'
import * as S from './CallCard.styles'

interface CallCardProps {
  call: GuestInboxCall
  at?: IDate | null
  checkIn?: IDate | null
  checkout?: IDate | null
  guestName?: string
}

const SUMMARY_CLIP = 420
const GUEST_SPEAKER = /customer|guest/i

export const CallCard = ({ call, at, checkIn, checkout, guestName }: CallCardProps) => {
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)

  const isResolved = call.resolution === CallResolution.resolved
  const turns = call.turns ?? []
  const turnCount = turns.length
  const hasTranscript = turnCount > 0 || !!call.transcript

  const assignee = call.assignee?.trim()
  const fromGreeting = call.agentOnCall?.trim()
  const agentName = assignee || fromGreeting || 'Agent'
  const nameSource = assignee
    ? "The agent is named from the Zendesk ticket's assignee"
    : fromGreeting
      ? 'The agent is named from the call greeting'
      : undefined

  return (
    <S.Root>
      <S.Head>
        <S.Chip>CALL</S.Chip>
        <S.Topic>{call.topic || 'Guest call'}</S.Topic>
        <S.Spacer />
        {call.sentimentRating != null ? (
          <Pill $tone="warn">{call.sentimentRating}/5 CSAT</Pill>
        ) : null}
        {call.resolution ? (
          <Pill $tone={isResolved ? 'good' : 'bad'}>
            {isResolved ? 'solved on call' : 'unresolved on call'}
          </Pill>
        ) : null}
        <S.When>{getEventWhen(at ?? call.at, checkIn, checkout)}</S.When>
      </S.Head>

      {call.summary ? <S.Summary>{clipText(call.summary, SUMMARY_CLIP)}</S.Summary> : null}

      <S.Footer>
        <span>{agentName} · summary extracted by the transcript model</span>
        <S.Spacer />
        {hasTranscript ? (
          <S.Toggle type="button" onClick={() => setIsTranscriptOpen(current => !current)}>
            {isTranscriptOpen
              ? 'Hide transcript'
              : turnCount
                ? `Read transcript · ${turnCount} turn${pluralizeS(turnCount)} →`
                : 'Read transcript →'}
          </S.Toggle>
        ) : (
          <S.NoTranscript>no transcript captured</S.NoTranscript>
        )}
        {call.ticketId ? (
          <S.Link href={zendeskTicketUrl(String(call.ticketId))} target="_blank" rel="noopener">
            Zendesk ↗
          </S.Link>
        ) : null}
      </S.Footer>

      {isTranscriptOpen && hasTranscript ? (
        <S.TranscriptBody>
          {turnCount ? (
            turns.map((turn, index) => {
              const isGuest = GUEST_SPEAKER.test(turn.who ?? '')

              return (
                <S.Turn key={`${turn.who ?? ''}-${index}`}>
                  <S.TurnSpeaker $isGuest={isGuest}>
                    {isGuest ? (guestName ?? 'Guest') : agentName}
                  </S.TurnSpeaker>
                  <S.TurnText>{turn.text}</S.TurnText>
                </S.Turn>
              )
            })
          ) : (
            <S.Transcript>{call.transcript}</S.Transcript>
          )}

          <S.TranscriptNote>
            Auto-transcribed, so wording is approximate.{' '}
            {nameSource
              ? `${nameSource} — the audio does not reliably name who spoke.`
              : 'No agent name on the ticket.'}
          </S.TranscriptNote>
        </S.TranscriptBody>
      ) : null}
    </S.Root>
  )
}
